import React, {useState} from 'react';
import moment from 'moment';
import round from 'lodash/round';
import uniq from 'lodash/uniq';
import {useNavigate} from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DollarIcon from '@mui/icons-material/Paid';
// import Grid from '@mui/material/Grid';
import LocationIcon from '@mui/icons-material/LocationOn';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import {useGetSchedulesQuery} from '../../redux/services/scheduleApi';
import {useGetReviewsQuery} from '../../redux/services/reviewApi';
// import {useGetSessionTypesQuery} from '../../redux/services/sessionTypeApi';
import {
  formatProviderName,
  getProviderUrl,
} from '../../helpers/providers';
import {getLocationTypes, getServiceCategories} from '../../helpers/selectOptions';
import getServiceIcon from '../availabilities/getServiceIcon';
import {useGetHighlightsQuery} from '../../redux/services/providerHighlightApi';
import {useGetProvidersQuery} from '../../redux/services/providerApi';

import CustomDialog from '../common/CustomDialog';
import ProviderAvatar from '../providers/ProviderAvatar';
import TimeSlot from '../availabilities/TimeSlot';
import ScheduleSpecificProviderForm from './ScheduleSpecificProviderForm';
import EmbeddedRating from '../embed/EmbeddedRating';
import ViewProvider from '../providers/ViewProvider';

const locationTypes = getLocationTypes();
const serviceCategories = getServiceCategories();

const Label = props => (
  <Box 
    style={{
      borderRadius: '8px',
      border: '1px solid rgb(240,240,240)',
      padding: '0.25em',
      marginTop: '0.25em',
      marginRight: '0.25em',
    }}>
      <Stack direction='row' alignItems='center' spacing={0} gap={0}>
        <Typography variant='body2'>
          {props.children}
        </Typography>
      </Stack>
  </Box>
);

const ProviderProfile = props => {
  const {
    provider, 
    onClose, 
    profileIsOpen, 
    reviews
  } = props;
  const id = props?.provider?.id;
  const providerQuery = useGetProvidersQuery(
    {id, is_hidden: false},
    {skip: !id}
  );
  const providerData = providerQuery?.data?.[0] || provider;
  const highlightsQuery = useGetHighlightsQuery(null, {skip: false});
  const highlights = highlightsQuery?.data || [];
  return (
    <CustomDialog
      fullWidth={true}
      maxWidth='lg'
      title={'Profile - ' + formatProviderName(provider)}
      open={profileIsOpen}
      onClose={onClose}
    >
      <ViewProvider
        provider={providerData}
        highlights={highlights || []}
        reviews={reviews || []}
        disableRequestSession={true}
        disableAddReview={true}
        isPopup={true}
      />
    </CustomDialog>
  );
};

const SchedulingProviderItem = ({
  provider, 
  day,
  location,
  serviceCategory,
  availabilities = [],
  date = null,
  onTimeSelect,
  onScheduleManually,
  hideSlots = false,
  unavailable = false,
  onSendMessage = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileIsOpen, setProfileIsOpen] = useState(false);
  const query = {
    service_category: serviceCategory,
    day: day,
    lat: location.lat,
    lon: location.lon,
    distance: location.distance || 30,
    start_time: moment(date).startOf('day').toISOString(),
    end_time: moment(date).endOf('day').toISOString(),
  };
  const navigate = useNavigate();
  const reviewQuery = useGetReviewsQuery(
    {provider_id: provider.id}
  );
  const reviews = reviewQuery?.data || [];
  const scheduleQuery = useGetSchedulesQuery(query);
  const slots = (scheduleQuery?.data?.data || []).filter(
    s => s.provider_availability.provider_id === provider.id
  );
  const highlights = [];
  return (
    <Box
      style={{
        borderRadius: '8px',
        padding: '1em',
        width: '100%',
        // cursor: 'pointer',
      }}
      sx={{
        border: '1px solid rgb(240,240,240)',
        borderLeft: `4px solid ${config().colors.primary}`,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        backgroundColor: 'rgb(250,250,250)',
        // '&:hover': {
        //   // border: `1px solid ${config().colors.primary}`,
        //   // borderLeft: `4px solid ${config().colors.primary}`,
        //   boxShadow: '0 3px 6px rgba(0,0,0,0.2)',
        //   backgroundColor: 'grey.100',
        // }
      }}
    >
      <Stack direction='row' spacing={1}>
        <ProviderAvatar provider={provider} />
        <Stack spacing={1} style={{width: '100%'}} flexGrow={1}>
          <Stack 
            direction='row' 
            spacing={0.5}
            alignItems='center' 
            justifyContent='space-between'
            flexGrow={1}
          >
            {/* <Stack spacing={1} alignItems='left'> */}
              <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={1} flexGrow={1}>
                {/* <Stack direction='row' spacing={1}> */}
                  <Stack>
                    <Typography variant='h3'>
                      {formatProviderName(provider)}
                    </Typography>
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                      <EmbeddedRating provider={{...provider, reviews}} />
                    </Stack>
                      {provider?.rate
                        ? <Typography color='text.muted' fontWeight='medium'>
                          <Stack direction='row' gap={0.5} alignItems='center'>
                            <DollarIcon style={{fontSize: '1.25em'}} />
                            From ${round(provider.rate, 0)}/session
                          </Stack>
                        </Typography>
                        : null
                      }
                  </Stack>
                {/* </Stack> */}
                <Button
                  size='small'
                  onClick={() => setProfileIsOpen(true)}
                >
                  Learn More
                </Button>
              </Stack>
            </Stack>
          {/* </Stack> */}
        </Stack>
      </Stack>
      <Stack spacing={1} mt={1}>
          {provider.specializes_in
            ? <Typography variant='body1' fontWeight='medium'>
              <span style={{}}>Specializes in</span>{' '}
              {provider.specializes_in}
            </Typography>
            : null
          }
          {/* Service Categories */}
          <Stack direction='row' spacing={0} flexWrap='wrap' alignItems='center'>
            {/* <Typography color='text.muted'>Services: </Typography> */}
            {
              uniq(availabilities.flatMap(av => av.configs.map(c => c.session_type.service_category)))
                .map(serviceCategory => serviceCategories.find(sc => sc.value === serviceCategory))
                .filter(sc => !!sc)
                .map(({label, value, colorName}, index) => {
                  const ServiceIcon = getServiceIcon({service: value});
                  return (
                    // <Stack 
                    //   key={provider.id + value} 
                    //   direction='row' 
                    //   spacing={0.5}
                    //   alignItems='center'
                    // >
                    <Box>
                      <Label key={provider.id + value}>
                        {/* <ServiceIcon color={colorName} fontSize='small' /> */}
                        <Typography variant='body2'>{label}</Typography>
                      </Label>
                    </Box>
                    // </Stack>
                  );
              })
            }
          </Stack>
          {/* Location Types */}
          <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
              <LocationIcon color='light' fontSize='small'/>
              {uniq(availabilities.flatMap(av => av.configs.flatMap(c => c.location_types)))
                .map(locationType => locationTypes.find(lt => lt.value === locationType))
                .filter(lt => !!lt)
                .map(({label, value}) => 
                  <Label key={provider.id + value}>
                    {label}
                  </Label>
                )
            }
          </Stack>
          {!hideSlots && !slots?.length && !scheduleQuery.isFetching &&
            <Stack>
              <Typography color='text.muted' fontStyle='italic'>
                <strong>Fully booked:</strong> No availability for selected date.
              </Typography>
              <Button
                size='small'
                variant='contained'
                onClick={onSendMessage || onScheduleManually}
              >
                Coordinate Directly
              </Button>
            </Stack>
          }
          {scheduleQuery.isFetching && 
            <Stack direction='row' spacing={1} alignItems='center' justifyContent='center' mt={1}>
              <CircularProgress size='2em' />
              <Typography color='text.muted'>Loading schedule</Typography>
            </Stack>
          }
      </Stack>
      <Stack direction='row' spacing={1} flexWrap='wrap' mt={2}>
        {!hideSlots && !scheduleQuery.isFetching && slots.map(s => 
          <Box style={{paddingTop: '0.5em'}}>
            <TimeSlot
              key={s.start_time}
              timeSlot={s}
              color='primary'
              outlined={true}
              onClick={(e) => {
                const selectedDate = moment(s.start_time);
                const data = {
                  data: selectedDate.toDate(),
                  isoString: selectedDate.toISOString(),
                  sessionTypeId: s.provider_availability.configs.find(c =>
                    c.session_type.service_category === serviceCategory
                  )?.session_type.id,
                  providerId: provider.id,
                  providerAvailabilityId: s.provider_availability.id
                };
                onTimeSelect(e, data);
              }}
            />
          </Box>
        )}
        {hideSlots &&
          <Stack direction='row' gap={1} width='100%'>
            <Box width='50%'>
              <Button
                size='small'
                variant='contained'
                fullWidth
                onClick={() => setIsOpen(true)}
              >
                Book Now
              </Button>
            </Box>
            <Box width='50%'>
              <Button
                variant='contained'
                size='small'
                fullWidth
                onClick={
                  onSendMessage
                  ? onSendMessage
                  : onScheduleManually
                }
              >
                Send a Message
              </Button>
            </Box>
          </Stack>
        }
        {hideSlots &&
          <CustomDialog
            title={`Book with ${formatProviderName(provider)}`}
            open={isOpen}
            onClose={() => setIsOpen(false)}
          >
            <ScheduleSpecificProviderForm 
              providerId={provider.id}
              serviceCategory={serviceCategory}
              lat={location.lat}
              lon={location.lon}
              distance={location.distance}
              onSendMessage={onSendMessage}
              onSubmit={(e, data) => {
                setIsOpen(false);
                onTimeSelect(e, data);
              }}
            />
          </CustomDialog>
        }
      </Stack>
      <ProviderProfile
        provider={provider}
        reviews={reviews}
        profileIsOpen={profileIsOpen}
        onClose={() => setProfileIsOpen(false)}
      />
    </Box>
  );
};

export default SchedulingProviderItem;